import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import jsQR from 'jsqr';
import { reportError, reportHeartbeat, flushErrors, HEARTBEAT_MS, deviceId } from '../lib/telemetry';

const API = process.env.REACT_APP_API_URL || '';

// ── Presupuesto de análisis (pensado para Android de gama baja, que es el
// hardware de la puerta) ──
//
// DÓNDE ESTÁ EL COSTO REAL (medido en el Redmi Note 10 de la puerta, no estimado):
//   captura (drawImage + getImageData) = 19,8 ms   ← el 77% del costo
//   decodificación (jsQR)              =  5,9 ms   ← solo el 23%
// O sea que el cuello NO es decodificar el QR sino leer los píxeles de la GPU a la
// CPU, que tiene un costo fijo alto. Dos consecuencias importantes:
//   1) Un Web Worker se llevaría apenas el 23% (jsQR es lo único que puede irse; el
//      drawImage/getImageData necesitan el hilo principal) → DESCARTADO por medición.
//   2) La única palanca lineal y barata es la FRECUENCIA: la ocupación del hilo es
//      directamente proporcional a los análisis por segundo.
//
// 6 análisis/s dejan el hilo en ~16% (contra 21% a 8/s) sin tocar la capacidad de
// lectura: cada análisis lee exactamente igual, solo hay menos oportunidades por
// segundo, y una persona sostiene el QR 1-2 s = 6-12 intentos, de sobra.
const ANALYSIS_FPS = 6;
const ANALYSIS_INTERVAL_MS = 1000 / ANALYSIS_FPS;

// RITMO ADAPTATIVO. Los 6 análisis/s se midieron en un Redmi Note 10 (p95 38,9 ms →
// 16% de hilo). En la noche del 8/8 apareció en la puerta un teléfono con p95 de
// 316 ms: 316 × 6 = 1,9 s de CPU por segundo de reloj, imposible. El hilo principal
// quedaba al 80% y, como la respuesta del fetch TAMBIÉN se procesa en ese hilo, el
// escáner cortaba por timeout a los 2,5 s aunque el servidor hubiera contestado en
// 200 ms. Se registraron 15 scan_timeout en un minuto.
//
// Con menos análisis por segundo el QR se lee una fracción de segundo más tarde,
// pero se lee: hoy, saturado, tarda MÁS y encima falla. Una persona sostiene el QR
// 1-2 s, así que a 3/s siguen siendo 3-6 intentos.
const FPS_ESCALONES = [6, 4, 3];          // nunca por debajo de 3
const SUBIR_SI_P95_MENOR = 55;            // holgado: no oscilar por un pico aislado
const BAJAR_SI_P95_MAYOR = 80;            // el Redmi bueno está en 39
const MUESTRAS_PARA_DECIDIR = 20;         // ~3-6 s de análisis antes de tocar nada
// Lado del canvas de análisis. Bajar de 320 a 240 NO empeora la lectura: en un banco
// de 37 condiciones x 24 repeticiones (distancia, poca luz, desenfoque, ruido,
// ángulo, papel arrugado) 240 leyó 81% vs 76% de 320 — reducir la escala promedia
// píxeles y eso filtra ruido antes de la binarización, así que con poca luz o sombra
// lee MEJOR. Único caso donde pierde: QR lejos Y borroso a la vez.
// Ojo con el motivo: 240 ayuda sobre todo porque hay MENOS BYTES que leer de la GPU
// (ver arriba), no porque jsQR se acelere mucho.
const ANALYSIS_SIZE = 240;
// Porción central del frame que se analiza (el recuadro guía es el 68%; tomamos
// un poco más para tolerar encuadres imperfectos).
const ROI_FRACTION = 0.76;
// 1 de cada N análisis mira el frame COMPLETO (escalado), por si el QR quedó
// fuera del recuadro: así el recorte no nos hace perder lecturas.
const FULL_FRAME_EVERY = 4;

// El PRIMER análisis (y los primeros) son artificialmente lentos: el JIT todavía
// no compiló jsQR. Se descartan, o el veredicto saldría mal.
const WARMUP_DISCARD = 5;
// Mínimo de muestras para opinar. Con menos, cualquier número es ruido.
const MIN_SAMPLES = 15;

// Mensaje ÚTIL según por qué falló la cámara. En la puerta hay que resolverlo en
// segundos, así que decimos la causa real en vez de un "no se pudo acceder".
function cameraErrorMessage(err) {
  const n = err && err.name;
  if (n === 'NotAllowedError' || n === 'SecurityError')
    return 'Falta el permiso de cámara. Dáselo desde el candado en la barra de direcciones y reintentá.';
  if (n === 'NotReadableError' || n === 'TrackStartError' || n === 'AbortError')
    return 'La cámara está ocupada por otra app o por OTRA PESTAÑA de este escáner. Cerrá las demás pestañas y reintentá.';
  if (n === 'NotFoundError' || n === 'DevicesNotFoundError')
    return 'No se encontró ninguna cámara en este dispositivo.';
  return 'No se pudo abrir la cámara. Reintentá.';
}

// Semáforo de rendimiento, con la ACCIÓN que corresponde. Ya no habla del Web
// Worker porque quedó descartado por medición (se llevaría solo el 23% del costo):
// la palanca real es bajar los análisis por segundo, que reduce la ocupación en
// forma lineal y no toca la capacidad de lectura.
//  - p95 de ms por análisis: es el pico, lo que se siente como tirón.
//  - ocupación: qué % del hilo principal se come el análisis.
//  - análisis/s: se compara contra el OBJETIVO (no contra un número fijo), si no un
//    objetivo bajo daría falsa alarma.
function perfVerdict({ p95, occupancy, aps }) {
  if (p95 > 100 || occupancy > 60 || (aps > 0 && aps < ANALYSIS_FPS * 0.7)) {
    return { txt: 'LENTO: bajar análisis/s (y resolución si no alcanza)', color: '#ff5252' };
  }
  if (p95 > 50 || occupancy > 40) {
    return { txt: 'AJUSTADO: conviene bajar 1-2 análisis/s', color: '#ffb300' };
  }
  return { txt: 'RENDIMIENTO OK', color: '#2ecc71' };
}

// Si el backend no contesta en este tiempo, cortamos y validamos con la lista
// offline. En la puerta, con fila, esperar más que esto es inaceptable — y el
// corte es seguro porque el backend es idempotente (un scan ya marcado vuelve
// como 'already_used' al sincronizar, no se duplica).
const SCAN_TIMEOUT_MS = 2500;
// Mismo QR delante de la cámara: no lo re-procesamos por este tiempo. Hace falta
// porque ya NO apagamos la cámara entre escaneos (antes el reinicio hacía de freno).
const SAME_QR_COOLDOWN_MS = 3000;
// Al sincronizar, un 'already_used' marcado a menos de esta distancia de MI escaneo
// es mi propio escaneo (el backend lo registró y el timeout me cortó la respuesta),
// no un QR repetido. Con el timeout en 2,5 s este margen es amplio de sobra.
const DUP_SAME_SCAN_MS = 30000;

export default function DoorScan() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [error, setError] = useState('');
  // idle = cámara apagada | scanning = analizando | verifying = consultando backend
  // | result = mostrando resultado. En verifying y result la CÁMARA SIGUE VIVA:
  // solo se pausa el análisis, así "Escanear siguiente" es instantáneo.
  const [mode, setMode] = useState('idle');
  const [result, setResult] = useState(null);       // { ok, reason, ticket, qr }
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);        // escaneos offline sin sincronizar
  // Error de CÁMARA: recuperable, con botón Reintentar. Distinto de `error`, que es
  // terminal (token inválido) — antes un fallo de cámara mataba la pantalla entera.
  const [camError, setCamError] = useState('');
  const [noBackup, setNoBackup] = useState(false);  // no se pudo guardar la lista offline
  const [dupes, setDupes] = useState([]);           // QR que ya estaban usados al sincronizar
  const [streamGen, setStreamGen] = useState(0);    // fuerza re-atar el stream nuevo
  const wantScanRef = useRef(false);                // el operador quiere estar escaneando

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const analyzeRef = useRef(false);       // gate del loop: ¿analizar este frame?
  const audioCtxRef = useRef(null);       // UN solo AudioContext para toda la sesión
  const lastRef = useRef({ qr: '', at: 0 });
  const handleScanRef = useRef(null);     // evita closure vieja dentro del loop
  const ctxRef = useRef(null);            // contexto 2D reutilizado
  const lastAnalysisRef = useRef(0);      // throttle
  // Ritmo actual del análisis. Vive en refs y no en estado: lo lee el loop en cada
  // cuadro, y un re-render por cambiar de escalón sería más caro que el ajuste.
  const fpsIdxRef = useRef(0);                              // índice en FPS_ESCALONES
  const intervaloRef = useRef(ANALYSIS_INTERVAL_MS);
  const [fpsActual, setFpsActual] = useState(ANALYSIS_FPS); // solo para mostrarlo
  const statsRef = useRef({ count: 0, samples: [], stamps: [], camRes: '', mode: '', capSamples: [], qrSamples: [] });
  const scansRef = useRef(0);             // escaneos hechos en este dispositivo
  const pendingRef = useRef(0);           // espejo de `pending` para leerlo en el latido

  // Modo diagnóstico: ?debug=1 muestra las métricas y el veredicto del Worker.
  // ?debug=1&legacy=1 analiza como ANTES (resolución completa, sin throttle) para
  // poder comparar antes/después en el mismo teléfono.
  const q = new URLSearchParams(window.location.search);
  const debugOn = q.get('debug') === '1';
  const legacyMode = debugOn && q.get('legacy') === '1';
  // ?notel=1 apaga el LATIDO, que es lo único periódico de la telemetría (los
  // listeners de error no consumen nada mientras no se dispare un error). Existe
  // para poder medir el piso del escáner sin telemetría y atribuir bien el costo:
  // sin esto no hay forma de saber si una ocupación más alta viene del análisis o
  // del reporte.
  const noTelemetry = q.get('notel') === '1';
  const [dbg, setDbg] = useState(null);

  // refs de datos offline
  const localRef = useRef(null);   // Map qr_code -> { status, buyer_name, buyer_lastname, lot_name }
  const cacheKey = `tk_scan_cache_${token}`;
  // Estado del refresco del padrón: desde cuándo pedir cambios y cuántas entradas
  // teníamos (si bajan, alguien borró algo y hay que podar).
  const padronDesde = useRef(null);
  const padronN = useRef(0);
  const padronAt = useRef(0);      // ms del último refresco exitoso
  const ultimoScanAt = useRef(0);  // para saber si la puerta está tranquila
  const oldQueueKey = `tk_scan_queue_${token}`;   // formato viejo (un array entero)
  const qPrefix = `tk_scanq_${token}_`;           // formato nuevo: UNA clave por escaneo
  const seqRef = useRef(0);

  // ---- cola offline ----
  // Antes la cola se guardaba como un array completo: cada escaneo hacía
  // JSON.parse + stringify de TODA la cola, y localStorage es sincrónico (bloquea
  // el hilo). Con cientos de escaneos acumulados eso degradaba justo en el momento
  // del escaneo. Ahora cada escaneo escribe SU propia clave: costo O(1) y constante.
  const enqueueScan = useCallback((qr, at) => {
    try {
      localStorage.setItem(qPrefix + Date.now() + '_' + (seqRef.current++), JSON.stringify({ qr_code: qr, scanned_at: at }));
    } catch { /* almacenamiento lleno: el escaneo ya fue permitido, no lo frenamos */ }
    setPending(p => p + 1);
  }, [qPrefix]);

  // Junta los escaneos pendientes (solo se recorre al sincronizar, no al escanear).
  const readQueue = useCallback(() => {
    const out = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k || k.indexOf(qPrefix) !== 0) continue;
        try { out.push({ key: k, scan: JSON.parse(localStorage.getItem(k)) }); } catch { out.push({ key: k, scan: null }); }
      }
    } catch {}
    return out;
  }, [qPrefix]);

  // ---- carga inicial: sesión + lista offline ----
  useEffect(() => {
    fetch(`${API}/api/scanning/public/${token}`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(setSession)
      .catch(() => setError('Link de escaneo inválido o expirado.'));

    fetch(`${API}/api/scanning/public/${token}/tickets`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(list => {
        const map = {};
        list.forEach(t => { map[t.qr_code] = t; });
        localRef.current = map;
        padronDesde.current = new Date().toISOString();
        padronN.current = list.length;
        padronAt.current = Date.now();
        // El respaldo offline es lo que salva la puerta si se cae la red. Si no
        // entra en el almacenamiento, hay que AVISARLO: antes fallaba en silencio
        // y el operador se enteraba recién cuando ya no había red.
        try { localStorage.setItem(cacheKey, JSON.stringify(list)); setNoBackup(false); }
        catch { setNoBackup(true); }
      })
      .catch(() => {
        // sin red: levantar cache previo
        try {
          const cached = JSON.parse(localStorage.getItem(cacheKey) || '[]');
          const map = {}; cached.forEach(t => { map[t.qr_code] = t; });
          localRef.current = map;
          if (!cached.length) setNoBackup(true);
        } catch { localRef.current = {}; setNoBackup(true); }
      });

    // Migra una cola del formato viejo (array único), para no perder escaneos que
    // hayan quedado sin sincronizar de una noche anterior.
    try {
      const old = localStorage.getItem(oldQueueKey);
      if (old) {
        (JSON.parse(old) || []).forEach((s, i) => {
          if (s && s.qr_code) localStorage.setItem(qPrefix + 'mig_' + i, JSON.stringify(s));
        });
        localStorage.removeItem(oldQueueKey);
      }
    } catch {}

    setPending(readQueue().length);
  }, [token]); // eslint-disable-line

  // ── Refresco del padrón ──
  //
  // La lista se bajaba UNA vez al abrir el escáner. Con eso, una entrada vendida en
  // boletería a las 2am no figuraba, y un QR borrado después seguía entrando si el
  // teléfono estaba sin red.
  //
  // El cuidado está en CUÁNDO pedirlo, no en el pedido en sí. La noche pasada hubo 44
  // timeouts de escaneo a las 03:56 y 12 ventas del TPV que quedaron en cola: la red del
  // boliche se satura justo en el pico. Así que este refresco cede el paso — solo sale
  // cuando la puerta está tranquila, y recién se impone si la lista quedó MUY vieja.
  const refrescarPadron = useCallback(async () => {
    if (!navigator.onLine || !padronDesde.current) return;
    const ahora = Date.now();
    const quieto = ahora - ultimoScanAt.current > 20000;   // 20 s sin escanear
    const viejo = ahora - padronAt.current > 10 * 60000;   // o ya pasaron 10 min
    if (!quieto && !viejo) return;
    try {
      const r = await fetch(`${API}/api/scanning/public/${token}/tickets/cambios`
        + `?desde=${encodeURIComponent(padronDesde.current)}&n=${padronN.current}`);
      if (!r.ok) return;
      const d = await r.json();
      padronDesde.current = d.hasta || padronDesde.current;
      padronAt.current = Date.now();
      if (d.sin_cambios) return;

      const map = localRef.current || {};
      (d.nuevos || []).forEach(t => {
        // Un escaneo nuestro que todavía está en la cola no se pisa: el servidor
        // todavía no lo sabe y volvería a decir 'pagado'.
        const local = map[t.qr_code];
        if (local && local.status === 'usado' && t.status !== 'usado') return;
        map[t.qr_code] = t;
      });
      if (Array.isArray(d.vigentes)) {
        const vivos = new Set(d.vigentes);
        Object.keys(map).forEach(qr => { if (!vivos.has(qr)) delete map[qr]; });
      }
      localRef.current = map;
      if (typeof d.n === 'number') padronN.current = d.n;
      try { localStorage.setItem(cacheKey, JSON.stringify(Object.values(map))); setNoBackup(false); }
      catch { setNoBackup(true); }
    } catch {
      // Sin red no pasa nada: se reintenta en la próxima vuelta y la lista vieja sigue
      // sirviendo. No se avisa para no llenar la puerta de mensajes que no puede accionar.
    }
  }, [token, cacheKey]);

  useEffect(() => {
    const id = setInterval(refrescarPadron, 120000);   // se evalúa cada 2 min; casi siempre no hace nada
    return () => clearInterval(id);
  }, [refrescarPadron]);

  const syncQueue = useCallback(async () => {
    const items = readQueue();
    if (!items.length || !navigator.onLine) return;
    const scans = items.map(i => i.scan).filter(s => s && s.qr_code);
    if (!scans.length) { items.forEach(i => { try { localStorage.removeItem(i.key); } catch {} }); setPending(0); return; }
    try {
      const r = await fetch(`${API}/api/scanning/public/${token}/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scans, device_id: deviceId() }),
      });
      if (!r.ok) {
        // Que el sync no pase es GRAVE: son ingresos hechos sin conexión que todavía
        // no están en el servidor. Queda registrado para verlo después.
        reportError({ token, source: 'scanner', kind: 'sync_fail', message: 'el servidor rechazo la sincronizacion de escaneos offline', route: '/api/scanning/public/:token/sync', context: { status: r.status, size: scans.length } });
        return;   // reintenta luego; NO borramos lo que no se confirmó
      }
      // ¿Algún QR ya estaba usado? Eso es el clásico QR pasado por la reja: dos
      // personas entrando con el mismo código por puertas distintas, que offline
      // no se puede detectar en el momento. Antes esta información se perdía.
      try {
        const data = await r.clone().json();
        const dups = [];
        for (const res of (data.results || [])) {
          if (res.reason !== 'already_used') continue;
          const mine = scans.find(s => s.qr_code === res.qr_code);
          const a = mine && mine.scanned_at ? Date.parse(mine.scanned_at) : null;
          const b = res.scanned_at ? Date.parse(res.scanned_at) : null;
          // Si ya estaba marcado casi a la misma hora que mi escaneo, es MI PROPIO
          // escaneo que el backend registró antes de que me cortara el timeout:
          // no es un duplicado real y avisarlo sería un falso positivo.
          if (a && b && Math.abs(a - b) < DUP_SAME_SCAN_MS) continue;
          dups.push({ qr: res.qr_code, name: res.name || null, at: res.scanned_at || null });
        }
        if (dups.length) setDupes(prev => [...prev, ...dups]);
      } catch { /* el aviso es informativo: si no se pudo leer, seguimos */ }
      // Borramos solo las claves que efectivamente mandamos (las que entraron
      // después siguen en cola).
      items.forEach(i => { try { localStorage.removeItem(i.key); } catch {} });
      setPending(readQueue().length);
    } catch { /* reintenta luego */ }
  }, [token, readQueue]);

  // Espejo de `pending` para que el latido lo lea sin re-crear el intervalo.
  useEffect(() => { pendingRef.current = pending; }, [pending]);

  useEffect(() => {
    // Al volver la red: primero los escaneos (son la operación), después los
    // errores encolados (son diagnóstico y pueden esperar).
    const goOnline = () => { setOnline(true); syncQueue(); setTimeout(flushErrors, 1500); };
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    if (navigator.onLine) syncQueue();
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, [syncQueue]);

  // ---- feedback (un AudioContext reutilizado: crear uno por escaneo agotaba el
  // límite del navegador y el sonido moría en silencio a mitad de la noche) ----
  const getAudioCtx = useCallback(() => {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    if (!audioCtxRef.current) {
      try { audioCtxRef.current = new AC(); } catch { return null; }
    }
    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') { try { ctx.resume(); } catch {} }
    return ctx;
  }, []);

  const feedback = useCallback((ok, unverified) => {
    try { navigator.vibrate?.(ok ? 80 : unverified ? [40, 40, 40] : [60, 50, 60]); } catch {}
    try {
      const ctx = getAudioCtx();
      if (!ctx) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = ok ? 880 : unverified ? 520 : 200;
      osc.type = ok ? 'sine' : 'square';
      gain.gain.setValueAtTime(0.18, ctx.currentTime);
      osc.start();
      osc.stop(ctx.currentTime + (ok ? 0.14 : 0.32));
      // El oscilador se libera solo al terminar; el contexto NO se cierra (se reutiliza).
    } catch {}
  }, [getAudioCtx]);

  // ---- cámara ----
  // Suelta el hardware pero NO cambia la intención del operador (se usa al pasar a
  // segundo plano, para no quedarnos la cámara tomada y poder recuperarla al volver).
  const releaseCamera = useCallback(() => {
    analyzeRef.current = false;
    if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => { t.onended = null; t.stop(); });
      streamRef.current = null;
    }
    const v = videoRef.current;
    if (v) { try { v.srcObject = null; } catch {} }
  }, []);

  // Apaga del todo (Cancelar / salir): suelta y deja de querer escanear.
  const stopCamera = useCallback(() => {
    wantScanRef.current = false;
    releaseCamera();
  }, [releaseCamera]);

  // Loop de análisis. CLAVE: reprograma el rAF ANTES de cualquier return, así
  // ningún early-return puede matar el loop (antes, un ref en null lo dejaba
  // muerto: el video seguía moviéndose pero ya no leía nada — parecía tildado).
  const tick = useCallback(() => {
    rafRef.current = requestAnimationFrame(tick);
    if (!analyzeRef.current) return;
    const t = performance.now();
    // Throttle: ~10 análisis/s alcanzan y dejan el hilo libre para la UI. Antes se
    // analizaba cada frame (hasta 60/s), que es lo que saturaba el teléfono.
    if (!legacyMode && t - lastAnalysisRef.current < intervaloRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    if (video.readyState !== video.HAVE_ENOUGH_DATA) return;
    const vw = video.videoWidth, vh = video.videoHeight;
    if (!vw || !vh) return;
    lastAnalysisRef.current = t;

    const s = statsRef.current;
    // Recorte + escala en UNA operación de drawImage (acelerada), así jsQR recibe
    // una imagen chica. El canvas tiene tamaño FIJO → nunca se realoca su buffer.
    let dw, dh, sx, sy, sw, sh, kind;
    if (legacyMode) {
      dw = vw; dh = vh; sx = 0; sy = 0; sw = vw; sh = vh; kind = 'full-res (legacy)';
    } else {
      const full = (s.count % FULL_FRAME_EVERY) === (FULL_FRAME_EVERY - 1);
      dw = ANALYSIS_SIZE; dh = ANALYSIS_SIZE;
      if (full) { sx = 0; sy = 0; sw = vw; sh = vh; kind = 'frame completo'; }
      else {
        const side = Math.min(vw, vh) * ROI_FRACTION;
        sw = side; sh = side; sx = (vw - side) / 2; sy = (vh - side) / 2; kind = 'ROI central';
      }
    }
    if (canvas.width !== dw) canvas.width = dw;
    if (canvas.height !== dh) canvas.height = dh;
    const ctx = ctxRef.current || (ctxRef.current = canvas.getContext('2d', { willReadFrequently: true }));
    if (!ctx) return;

    const t0 = performance.now();
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, dw, dh);
    const img = ctx.getImageData(0, 0, dw, dh);
    // Solo en modo debug medimos la CAPTURA por separado de la DECODIFICACIÓN. Es
    // el dato que decide si vale un Web Worker: el worker solo puede llevarse jsQR
    // (el drawImage/getImageData necesitan el hilo principal), así que si la captura
    // pesa mucho, mover jsQR rinde menos de lo que parece.
    const tCap = debugOn ? performance.now() : 0;
    const code = jsQR(img.data, dw, dh, { inversionAttempts: 'dontInvert' });
    const tEnd = performance.now();
    const dt = tEnd - t0;
    if (debugOn) {
      s.capSamples.push(tCap - t0);
      s.qrSamples.push(tEnd - tCap);
      if (s.capSamples.length > 60) { s.capSamples.shift(); s.qrSamples.shift(); }
    }

    // métricas (baratas: unos pocos números por análisis)
    s.count++;
    s.camRes = vw + '×' + vh;
    s.mode = kind;
    if (s.count > WARMUP_DISCARD) {          // descarta el warm-up del JIT
      s.samples.push(dt);
      if (s.samples.length > 60) s.samples.shift();
    }
    s.stamps.push(t);
    while (s.stamps.length && t - s.stamps[0] > 1000) s.stamps.shift();

    if (!code || !code.data) return;
    const qr = code.data.trim().toUpperCase();
    const now = Date.now();
    if (qr === lastRef.current.qr && now - lastRef.current.at < SAME_QR_COOLDOWN_MS) return;
    lastRef.current = { qr, at: now };
    analyzeRef.current = false;               // pausa el análisis, NO apaga la cámara
    handleScanRef.current?.(qr);
  }, [legacyMode]);

  // Pide la cámara. Devuelve '' si salió bien, o el mensaje de error a mostrar.
  const acquireCamera = useCallback(async () => {
    // Resolución MODESTA a propósito: sin constraint, muchos Android entregan
    // 1280×720 o más, y analizar eso en gama baja cuesta ~10× más sin leer mejor
    // (a 640×480 el QR ocupa ~200 px, de sobra).
    const ideal = {
      facingMode: 'environment',
      width: { ideal: 640 }, height: { ideal: 480 },
      frameRate: { ideal: 15, max: 30 },
    };
    let stream = null;
    try {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: ideal });
      } catch (e1) {
        if (e1 && (e1.name === 'NotAllowedError' || e1.name === 'NotReadableError' || e1.name === 'TrackStartError')) throw e1;
        // el equipo rechazó los constraints: reintentamos sin ellos
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      }
    } catch (err) {
      // La cámara que no abre es lo que más se sintió como "se tildó": queda
      // registrado con el nombre del error, que es lo que dice la causa real
      // (permiso, cámara ocupada por otra pestaña/app, o equipo sin cámara).
      reportError({ token, source: 'scanner', kind: 'camera_fail', message: 'no se pudo abrir la camara: ' + (err && err.name ? err.name : 'desconocido'), route: '/scan', context: { reason: err && err.name ? String(err.name) : 'unknown' } });
      return cameraErrorMessage(err);
    }
    streamRef.current = stream;
    // Si otra app/pestaña nos roba la cámara, el track muere y el video queda
    // congelado (se ve como "se tildó"). Lo detectamos y ofrecemos reintentar.
    stream.getVideoTracks().forEach(t => {
      t.onended = () => {
        if (streamRef.current === stream) {
          releaseCamera();
          setCamError('Se interrumpió la cámara (otra app la tomó). Reintentá.');
          setMode('idle');
          reportError({ token, source: 'scanner', kind: 'camera_lost', message: 'la camara se interrumpio durante el escaneo', route: '/scan' });
        }
      };
    });
    setStreamGen(g => g + 1);   // fuerza el efecto que ata el stream al <video>
    return '';
  }, []); // eslint-disable-line

  // Enciende la cámara. Si el stream ya está vivo (volviendo de un resultado),
  // solo reanuda el análisis → sin el reinicio de ~1s que costaba una persona de fila.
  const startCamera = useCallback(async () => {
    wantScanRef.current = true;
    setCamError('');
    if (streamRef.current) { setMode('scanning'); return; }
    const msg = await acquireCamera();
    if (msg) { setCamError(msg); setMode('idle'); return; }
    setMode('scanning'); // el <video> se monta en el render siguiente
  }, [acquireCamera]);

  // Ata el stream al <video> una vez montado y mantiene el loop vivo. Corre
  // post-render, así videoRef ya existe (antes era una carrera).
  useEffect(() => {
    if (mode === 'idle') return;
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) return;
    if (video.srcObject !== stream) {
      video.srcObject = stream;
      video.setAttribute('playsinline', 'true');
      video.play().catch(() => {});
    }
    if (rafRef.current == null) rafRef.current = requestAnimationFrame(tick);
  }, [mode, tick, streamGen]);

  // Cámara y segundo plano. En Android la cámara la tiene UNA sola pestaña: si la
  // dejamos tomada al pasar a background, cualquier otra pestaña (o app) se queda
  // sin cámara — y como el stream ahora persiste entre escaneos, eso bloqueaba
  // abrir el escáner de nuevo. Al ir a background la soltamos; al volver, si el
  // operador estaba escaneando, la recuperamos sola.
  useEffect(() => {
    const onVis = async () => {
      if (document.hidden) {
        if (streamRef.current) releaseCamera();
      } else if (wantScanRef.current && !streamRef.current) {
        const msg = await acquireCamera();
        if (msg) { setCamError(msg); setMode('idle'); }
        else setMode(m => (m === 'result' || m === 'verifying' ? m : 'scanning'));
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [acquireCamera, releaseCamera]);

  // El análisis solo corre en 'scanning'; en verifying/result la cámara queda viva.
  useEffect(() => { analyzeRef.current = (mode === 'scanning'); }, [mode]);

  // Lee las métricas que el loop ya viene acumulando en un ref. NO mide nada nuevo:
  // por eso puede llamarla tanto el panel de debug (1/s) como el latido (1/min) sin
  // agregar costo al análisis.
  const computeStats = useCallback(() => {
    const s = statsRef.current;
    const n = s.samples.length;
    if (!n) return null;
    const sorted = [...s.samples].sort((a, b) => a - b);
    const avg = sorted.reduce((a, b) => a + b, 0) / n;
    const p95 = sorted[Math.min(n - 1, Math.floor(n * 0.95))];
    const aps = s.stamps.length;                       // análisis en el último segundo
    const occupancy = (avg * aps) / 10;                // % del hilo principal
    const mean = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);
    return { n, avg, p95, aps, occupancy, camRes: s.camRes, kind: s.mode, count: s.count,
      cap: mean(s.capSamples), qr: mean(s.qrSamples) };
  }, []);

  // AJUSTE DEL RITMO. Corre 1 vez por segundo sobre las métricas que el loop ya
  // acumula, así que no agrega costo al análisis. Baja un escalón si el teléfono no
  // llega, y sube si sobra margen — con una banda muerta entre 55 y 80 ms para que no
  // oscile con un pico aislado. En modo legacy no se toca (ese modo existe para
  // comparar contra el comportamiento viejo, ajustarlo lo invalidaría).
  useEffect(() => {
    if (legacyMode) return undefined;
    const id = setInterval(() => {
      if (!analyzeRef.current) return;
      const st = computeStats();
      if (!st || st.n < MUESTRAS_PARA_DECIDIR) return;
      const i = fpsIdxRef.current;
      let nuevo = i;
      if (st.p95 > BAJAR_SI_P95_MAYOR && i < FPS_ESCALONES.length - 1) nuevo = i + 1;
      else if (st.p95 < SUBIR_SI_P95_MENOR && i > 0) nuevo = i - 1;
      if (nuevo === i) return;
      fpsIdxRef.current = nuevo;
      intervaloRef.current = 1000 / FPS_ESCALONES[nuevo];
      setFpsActual(FPS_ESCALONES[nuevo]);
      // Las muestras viejas son de otro ritmo: si no se limpian, el promedio arrastra
      // el estado anterior y el próximo ajuste decide con datos que ya no valen.
      statsRef.current.samples = [];
      statsRef.current.capSamples = [];
      statsRef.current.qrSamples = [];
    }, 1000);
    return () => clearInterval(id);
  }, [computeStats, legacyMode]);

  // Métricas del modo debug: se calculan 1 vez por segundo (no en cada análisis,
  // para que medir no altere lo que se mide).
  useEffect(() => {
    if (!debugOn) return undefined;
    const id = setInterval(() => {
      const st = computeStats();
      if (!st) { setDbg({ vacio: true }); return; }
      // El veredicto solo se emite con muestras suficientes Y mientras está
      // analizando de verdad: si no, un único análisis daría un fallo falso.
      const solid = st.n >= MIN_SAMPLES && analyzeRef.current && st.aps > 0;
      setDbg({
        camRes: st.camRes, kind: st.kind, count: st.count, samples: st.n,
        avg: Math.round(st.avg * 10) / 10,
        p95: Math.round(st.p95 * 10) / 10,
        aps: st.aps, occupancy: Math.round(st.occupancy), fpsObjetivo: fpsActual,
        cap: st.cap == null ? null : Math.round(st.cap * 10) / 10,
        qr: st.qr == null ? null : Math.round(st.qr * 10) / 10,
        // Lo que el Worker podría sacar del hilo: la parte de jsQR. La captura se
        // queda igual porque necesita el hilo principal.
        workerGain: (st.cap != null && st.qr != null && (st.cap + st.qr) > 0)
          ? Math.round(st.qr / (st.cap + st.qr) * 100) : null,
        canvas: legacyMode ? st.camRes : (ANALYSIS_SIZE + '×' + ANALYSIS_SIZE),
        verdict: solid ? perfVerdict(st) : null,
      });
    }, 1000);
    return () => clearInterval(id);
  }, [debugOn, legacyMode, computeStats]);

  // ── Caja negra ──
  // Latido cada 60s, SOLO con la cámara activa (un escáner abierto sin usar no
  // escribe filas). Lee el ref y hace un fetch fire-and-forget: no toca el loop.
  useEffect(() => {
    if (noTelemetry) return undefined;
    const id = setInterval(() => {
      if (!analyzeRef.current) return;         // no está escaneando → no late
      const st = computeStats();
      reportHeartbeat({
        token, source: 'scanner',
        metrics: {
          online: navigator.onLine,
          scans_total: scansRef.current,
          queue_pending: pendingRef.current,
          // Antigüedad del padrón: si crece sin parar, este teléfono está validando
          // contra una lista vieja y no se enteró de las ventas de la noche.
          padron_edad_s: padronAt.current ? Math.round((Date.now() - padronAt.current) / 1000) : null,
          cam_res: st ? (st.camRes + ' @' + fpsActual + '/s') : null,
          aps: st ? Math.round(st.aps * 10) / 10 : null,
          ms_avg: st ? Math.round(st.avg * 10) / 10 : null,
          ms_p95: st ? Math.round(st.p95 * 10) / 10 : null,
          occupancy: st ? Math.round(st.occupancy * 10) / 10 : null,
        },
      });
    }, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [token, computeStats, noTelemetry, fpsActual]);

  // Errores de JS que nadie atrapó (incluye el "se rompió la pantalla" silencioso).
  useEffect(() => {
    const onErr = (e) => reportError({ token, source: 'scanner', kind: 'js_error', message: e?.message || 'error', route: '/scan' });
    const onRej = (e) => reportError({ token, source: 'scanner', kind: 'unhandled_rejection', message: e?.reason?.message || String(e?.reason || ''), route: '/scan' });
    window.addEventListener('error', onErr);
    window.addEventListener('unhandledrejection', onRej);
    return () => { window.removeEventListener('error', onErr); window.removeEventListener('unhandledrejection', onRej); };
  }, [token]);

  // Consulta al backend con timeout duro. Devuelve null si no se pudo verificar.
  // Devuelve { data, fallo } — `fallo` describe POR QUÉ no se pudo verificar, con el
  // tiempo REAL transcurrido. Antes se registraba la constante 2500 y un `online: true`
  // tautológico, y el catch mezclaba tres causas distintas: con eso era imposible saber
  // si el servidor tardó, si el pedido nunca salió, o si el hilo del teléfono estaba
  // tapado. Esa distinción es justo la que hizo falta para diagnosticar el 8/8.
  const verifyOnline = useCallback(async (qr) => {
    const ctrl = new AbortController();
    const t0 = (performance && performance.now) ? performance.now() : Date.now();
    const ahora = () => ((performance && performance.now) ? performance.now() : Date.now()) - t0;
    let abortado = false;
    const timer = setTimeout(() => { abortado = true; ctrl.abort(); }, SCAN_TIMEOUT_MS);
    let tRespuesta = null;
    try {
      const r = await fetch(`${API}/api/scanning/public/${token}/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qr_code: qr, device_id: deviceId() }),
        signal: ctrl.signal,
      });
      tRespuesta = ahora();               // llegaron las cabeceras: el servidor respondió
      const data = await r.json();
      return { data, ms: ahora(), msRespuesta: tRespuesta };
    } catch (e) {
      const ms = ahora();
      // ms_respuesta presente = el servidor SÍ contestó y lo que tardó fue leer/parsear
      // en el hilo principal (teléfono saturado). Ausente = nunca llegó respuesta.
      const causa = abortado
        ? (tRespuesta != null ? 'corte_procesando_respuesta' : 'timeout_sin_respuesta')
        : (e && e.name === 'TypeError' ? 'red_caida' : 'respuesta_ilegible');
      return { data: null, fallo: { causa, ms, msRespuesta: tRespuesta } };
    } finally {
      clearTimeout(timer);
    }
  }, [token]);

  // validación local contra la lista descargada
  const validateOffline = useCallback((qr) => {
    const map = localRef.current || {};
    const t = map[qr];
    // No está en la lista Y no pudimos preguntar al backend → NO sabemos si es
    // válido (pudo venderse después de la descarga). Decir "QR no válido" sería
    // mentir y dejar afuera a alguien que pagó: lo marcamos como no verificado.
    if (!t) return { ok: false, reason: 'unverified', unverified: true, offline: true, qr };
    // Solo traslado: vendió el micro, no la entrada. Se frena igual que online, con el
    // mismo mensaje, para que sin red la puerta no lo deje pasar.
    if (t.solo_traslado) return { ok: false, reason: 'solo_traslado', ticket: t, offline: true, qr };
    // auto_ingreso: se dio por ingresado al venderlo en boletería, la puerta nunca lo
    // escaneó. Este escaneo es el primero real, así que se acepta (igual que online).
    if (t.status === 'usado' && !t.auto_ingreso) return { ok: false, reason: 'already_used', ticket: t, offline: true, qr };
    t.auto_ingreso = false;
    // aceptar y marcar local + encolar para sync
    t.status = 'usado';
    t.scanned_at = new Date().toISOString();
    enqueueScan(qr, t.scanned_at);
    return { ok: true, ticket: t, offline: true, qr };
  }, [enqueueScan]);

  const handleScan = useCallback(async (qr) => {
    setMode('verifying');
    scansRef.current += 1;
    ultimoScanAt.current = Date.now();   // marca el pulso de la puerta: el refresco del padrón le cede el paso
    let res = null;
    if (navigator.onLine) {
      const out = await verifyOnline(qr);
      res = out.data;
      if (out.fallo) {
        // Se registra la causa y el tiempo REAL, más el estado del hilo en ese momento:
        // con eso la caja negra distingue "el servidor tardó" de "el teléfono no podía
        // procesar la respuesta", que es la diferencia que costó una noche de análisis.
        const st = computeStats();
        reportError({
          token, source: 'scanner', kind: 'scan_timeout',
          message: 'no se pudo verificar contra el servidor: ' + out.fallo.causa,
          route: '/api/scanning/public/:token/scan',
          context: {
            causa: out.fallo.causa,
            ms: Math.round(out.fallo.ms),
            ms_respuesta: out.fallo.msRespuesta == null ? null : Math.round(out.fallo.msRespuesta),
            tope_ms: SCAN_TIMEOUT_MS,
            fps: fpsActual,
            hilo_pct: st ? Math.round(st.occupancy) : null,
            p95_ms: st ? Math.round(st.p95) : null,
          },
        });
      }
    }
    if (!res) res = validateOffline(qr);
    if (!res.qr) res.qr = qr;
    setResult(res);
    setMode('result');
    feedback(!!res.ok, !!res.unverified);
  }, [verifyOnline, validateOffline, feedback, computeStats, fpsActual, token]);
  handleScanRef.current = handleScan;

  // Vuelve a escanear sin reabrir la cámara (el stream nunca se apagó).
  const scanNext = useCallback(() => { setResult(null); setMode('scanning'); }, []);

  // Limpieza total al salir: cámara + audio.
  useEffect(() => () => {
    stopCamera();
    const c = audioCtxRef.current;
    if (c) { try { c.close(); } catch {} audioCtxRef.current = null; }
  }, [stopCamera]);

  if (error) return <Centered><div style={{ color: 'var(--red-300)', textAlign: 'center', padding: 24 }}>{error}</div></Centered>;
  if (!session) return <Centered><div style={{ color: 'var(--ink-600)' }}>Cargando…</div></Centered>;

  const res = result;
  const t = res?.ticket;
  const usedTime = t?.scanned_at ? new Date(t.scanned_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Argentina/Buenos_Aires' }) : '';
  const resTitle = !res ? '' : res.ok ? 'Acceso permitido'
    : res.reason === 'already_used' ? 'Entrada ya utilizada'
    : res.reason === 'unverified' ? 'No se pudo verificar'
    : res.reason === 'cancelled' ? 'Entrada cancelada'
    // La persona pagó algo real: compró el micro, no la entrada. Decirle "QR no válido"
    // la trata de trucha cuando en realidad entra con otra entrada.
    : res.reason === 'solo_traslado' ? 'Solo traslado · no es entrada'
    : 'QR no válido';
  const resBg = !res ? '#000' : res.ok ? '#0c7a3f' : res.unverified ? '#8a5a00' : '#a30c12';

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 20px' }}>
      <style>{'@keyframes tkspin{to{transform:rotate(360deg)}}'}</style>
      {session.logo_url
        ? <img src={session.logo_url} alt={session.org_name || ''} style={{ height: 36, marginBottom: 10, objectFit: 'contain' }} />
        : <div style={{ marginBottom: 10, fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 18, color: '#fff', textTransform: 'uppercase' }}>{session.org_name || ''}</div>}
      <div className="lb-eyebrow">{session.kind === 'traslado' ? 'Control de traslado' : 'Control de puerta'}</div>
      <div className="lb-h" style={{ fontSize: 22, marginTop: 6, textAlign: 'center' }}>{session.event_name}</div>
      {session.kind === 'traslado' && <div style={{ marginTop: 6, fontFamily: 'var(--font-display)', fontWeight: 700, letterSpacing: '0.16em', fontSize: 12, color: 'var(--red-400)', border: '1px solid var(--red-500)', borderRadius: 999, padding: '4px 14px' }}>QR TRASLADO</div>}

      {/* estado de conexión */}
      <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
        <span style={{ color: online ? '#2ecc71' : 'var(--red-400)' }}>● {online ? 'En línea' : 'Sin conexión'}</span>
        {pending > 0 && <span style={{ color: 'var(--ink-600)' }}>· {pending} por sincronizar</span>}
      </div>
      {/* QR repetidos detectados al sincronizar: alguien pasó su QR por la reja y
          entró dos veces por puertas distintas. Offline no se puede frenar en el
          momento, pero al menos queda a la vista para que seguridad lo sepa. */}
      {dupes.length > 0 && (
        <div style={{ width: '100%', maxWidth: 420, marginTop: 12, border: '1px solid rgba(200,16,46,0.55)', background: 'rgba(200,16,46,0.12)', borderRadius: 6, padding: '12px 14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, color: 'var(--red-300)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {dupes.length === 1 ? '1 QR repetido' : `${dupes.length} QR repetidos`}
            </div>
            <button onClick={() => setDupes([])} style={{ background: 'transparent', border: 0, color: 'var(--ink-600)', fontSize: 18, lineHeight: 1, cursor: 'pointer', padding: 0 }}>✕</button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-700)', marginTop: 4, lineHeight: 1.5 }}>
            Ya habían ingresado antes (escaneados sin conexión):
          </div>
          {dupes.slice(0, 6).map((d, i) => (
            <div key={i} style={{ fontSize: 12, color: '#fff', marginTop: 4, fontFamily: 'monospace' }}>
              {d.qr}{d.name ? ` · ${d.name}` : ''}{d.at ? ` · ${new Date(d.at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Argentina/Buenos_Aires' })} hs` : ''}
            </div>
          ))}
          {dupes.length > 6 && <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 4 }}>y {dupes.length - 6} más…</div>}
        </div>
      )}

      {/* Si no hay lista offline, el escáner depende 100% de la red: el operador
          tiene que saberlo ANTES de que se caiga, no cuando ya se cayó. */}
      {noBackup && (
        <div style={{ marginTop: 8, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#d4af37', border: '1px solid rgba(212,175,55,0.5)', borderRadius: 999, padding: '4px 12px' }}>
          ⚠ Sin respaldo offline
        </div>
      )}

      <div style={{ width: '100%', maxWidth: 420, marginTop: 24, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {mode !== 'idle' ? (
          // El <video> queda montado también en verifying/result: mantener el stream
          // vivo es lo que hace instantáneo el "Escanear siguiente".
          <div style={{ position: 'relative', width: '100%', aspectRatio: '1', background: '#000', border: '1px solid var(--border)', borderRadius: 4, overflow: 'hidden' }}>
            <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted playsInline />
            <div style={{ position: 'absolute', inset: '16%', border: '2px solid rgba(255,255,255,0.7)', borderRadius: 4 }} />
            <button onClick={() => { stopCamera(); setResult(null); setMode('idle'); }} style={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.6)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 4, padding: '10px 18px', cursor: 'pointer' }}>Cancelar</button>
          </div>
        ) : (
          <>
            {camError && (
              <div style={{ border: '1px solid rgba(200,16,46,0.5)', background: 'rgba(200,16,46,0.10)', borderRadius: 6, padding: '14px 16px', marginBottom: 14, fontSize: 14, color: '#fff', lineHeight: 1.5 }}>
                {camError}
              </div>
            )}
            <button
              onClick={startCamera}
              className="lb-btn lb-btn--primary"
              style={{ width: '100%', padding: '28px', fontSize: 20, borderRadius: 4 }}
            >
              {camError ? 'Reintentar' : 'Escanear QR'}
            </button>
          </>
        )}
      </div>

      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 16, textAlign: 'center' }}>
        Apuntá la cámara al QR de la entrada.
      </div>

      {/* Modo diagnóstico (?debug=1): números para decidir el Worker en el
          teléfono real. Con &legacy=1 analiza como antes, para comparar. */}
      {debugOn && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, background: 'rgba(0,0,0,0.88)', color: '#fff', fontFamily: 'monospace', fontSize: 12, padding: '8px 10px', lineHeight: 1.55, borderBottom: '1px solid #333' }}>
          <div style={{ fontWeight: 700, letterSpacing: '0.08em', color: legacyMode ? '#ffb300' : '#7fd4ff' }}>
            DIAGNÓSTICO {legacyMode ? '· MODO VIEJO (full-res, sin throttle)' : '· OPTIMIZADO'}
          </div>
          {!dbg || dbg.vacio ? (
            <div style={{ color: '#9a9a9a' }}>Apretá "Escanear QR" y apuntá a una pared (sin QR) ~10 s…</div>
          ) : (
            <>
              <div>cámara <b>{dbg.camRes}</b> · analiza <b>{dbg.canvas}</b> · {dbg.kind}</div>
              <div>ms/análisis: prom <b>{dbg.avg}</b> · <b style={{ color: dbg.p95 > 100 ? '#ff5252' : dbg.p95 > 50 ? '#ffb300' : '#2ecc71' }}>p95 {dbg.p95}</b></div>
              {/* Se compara contra el objetivo VIGENTE, no contra 6: con el ritmo
                  adaptativo, 3 análisis/s puede ser exactamente lo correcto. */}
              <div>análisis/s: <b style={{ color: dbg.aps < (dbg.fpsObjetivo || 6) * 0.7 ? '#ff5252' : '#2ecc71' }}>{dbg.aps}</b>
                <span style={{ color: '#888' }}> /{dbg.fpsObjetivo}{dbg.fpsObjetivo < 6 ? ' (bajado solo)' : ''}</span>
                {' '}· hilo ocupado <b style={{ color: dbg.occupancy > 60 ? '#ff5252' : dbg.occupancy > 40 ? '#ffb300' : '#2ecc71' }}>{dbg.occupancy}%</b> · total {dbg.count}</div>
              {dbg.cap != null && (
                <div style={{ color: '#7fd4ff' }}>
                  captura <b>{dbg.cap}</b> + jsQR <b>{dbg.qr}</b> ms · el Worker se llevaría <b>{dbg.workerGain}%</b> del costo
                  <span style={{ color: '#9a9a9a' }}> (quedaría ~{Math.max(0, Math.round(dbg.occupancy * (100 - dbg.workerGain) / 100))}% de hilo)</span>
                </div>
              )}
              {dbg.verdict
                ? <div style={{ marginTop: 3, fontWeight: 700, color: dbg.verdict.color }}>{dbg.verdict.txt}</div>
                : <div style={{ marginTop: 3, color: '#9a9a9a' }}>Midiendo… apuntá a una pared SIN QR unos segundos ({dbg.samples}/{MIN_SAMPLES} muestras)</div>}
            </>
          )}
        </div>
      )}

      {/* Consultando al backend: antes la pantalla quedaba con el último frame
          congelado y sin ningún aviso — se leía como "se tildó". */}
      {mode === 'verifying' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18 }}>
          <div style={{ width: 44, height: 44, border: '3px solid rgba(255,255,255,0.25)', borderTopColor: '#fff', borderRadius: '50%', animation: 'tkspin 0.8s linear infinite' }} />
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Verificando…</div>
        </div>
      )}

      {/* Resultado como overlay (no desmonta el video) */}
      {mode === 'result' && res && (
        <div style={{ position: 'fixed', inset: 0, background: resBg, color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 28, textAlign: 'center' }}>
          <div style={{ fontSize: 96, lineHeight: 1, marginBottom: 18 }}>{res.ok ? '✓' : res.unverified ? '?' : '✕'}</div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 30, textTransform: 'uppercase', letterSpacing: '0.02em' }}>{resTitle}</div>
          {t && (t.buyer_name || t.buyer_lastname) && <div style={{ fontSize: 22, marginTop: 16, fontWeight: 600 }}>{t.buyer_name} {t.buyer_lastname || ''}</div>}
          {t && t.lot_name && <div style={{ fontSize: 16, opacity: 0.9, marginTop: 4 }}>{t.lot_name}</div>}

          {/* EDAD PARA LA BARRA. LOBBO es +15 por ordenanza, así que el menor ENTRA: lo
              que no se le puede vender es alcohol. Por eso esto marca y no bloquea, y
              va en blanco sobre negro — el fondo del resultado ya es verde o rojo según
              el QR, y un aviso del mismo color se confundiría con "entrada inválida".
              Los tres estados son distintos: sin dato no es lo mismo que mayor. */}
          {t && t.menor === true && (
            <div style={{ marginTop: 18, background: '#000', border: '3px solid #fff', borderRadius: 10, padding: '14px 22px', maxWidth: 380 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 26, letterSpacing: '0.04em' }}>⛔ MENOR DE 18</div>
              <div style={{ fontSize: 15, marginTop: 4, opacity: 0.95 }}>No vender alcohol</div>
            </div>
          )}
          {/* `undefined` además de null: una lista descargada ANTES de este cambio no
              trae el campo, y ahí corresponde "no sé" y no un silencio que se lee como
              "es mayor". */}
          {t && (t.menor === null || t.menor === undefined) && (
            <div style={{ marginTop: 16, background: 'rgba(0,0,0,0.45)', border: '2px solid rgba(255,255,255,0.7)', borderRadius: 8, padding: '10px 16px', maxWidth: 380 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, letterSpacing: '0.04em' }}>SIN EDAD CARGADA</div>
              <div style={{ fontSize: 13, marginTop: 2, opacity: 0.9 }}>Pedir documento antes de vender alcohol</div>
            </div>
          )}
          {!res.ok && res.reason === 'already_used' && usedTime && <div style={{ fontSize: 16, opacity: 0.9, marginTop: 10 }}>Ingresó {usedTime} hs</div>}
          {res.unverified && <div style={{ fontSize: 15, opacity: 0.95, marginTop: 14, maxWidth: 340, lineHeight: 1.5 }}>Sin conexión con el servidor y este QR no está en la lista descargada. Puede ser una venta reciente: reintentá o decidí en la puerta.</div>}
          {res.offline && !res.unverified && <div style={{ fontSize: 12, opacity: 0.8, marginTop: 14, letterSpacing: '0.1em', textTransform: 'uppercase' }}>● Validado offline</div>}
          {res.unverified && (
            <button
              onClick={() => handleScan(res.qr)}
              style={{ marginTop: 28, background: 'transparent', color: '#fff', border: '2px solid #fff', borderRadius: 4, padding: '16px 24px', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, textTransform: 'uppercase', cursor: 'pointer', width: '100%', maxWidth: 360 }}
            >
              Reintentar
            </button>
          )}
          <button
            onClick={scanNext}
            style={{ marginTop: res.unverified ? 12 : 40, background: '#fff', color: resBg, border: 0, borderRadius: 4, padding: '20px 28px', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18, textTransform: 'uppercase', cursor: 'pointer', width: '100%', maxWidth: 360 }}
          >
            Escanear siguiente
          </button>
        </div>
      )}
    </div>
  );
}

function Centered({ children }) {
  return <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg)' }}>{children}</div>;
}
